'use memo';

import {
  FormCondition,
  RenderedSpecialCondition,
  SpecialConditionData,
  useConstructConditionClauseText,
} from '@urbanx/agx.campaigns.client';
import { AgxEmptyState, AgxSpacer } from '@urbanx/agx.ui-components2';
import { TaskModalPageProps } from 'pages/allTasks/TaskModal/TaskModalPageProps';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AddSpecialConditionButton } from '../contract-information/modals/specialConditions/AddSpecialConditionButton';
import SpecialConditionCollapsiblePanel from '../contract-information/panels/SpecialConditionCollapsiblePanel';
import { withUpdatedSpecialConditionDueDate } from '../contract-information/specialConditionDueDate';

interface ContractComplianceSpecialConditionsProps extends TaskModalPageProps {
  specialConditions?: SpecialConditionData[];
  onSpecialConditionsUpdated?: (specialConditions: SpecialConditionData[]) => void;
  addButtonVariant?: 'primary' | 'hollow' | 'dark';
}

const EMPTY_SPECIAL_CONDITIONS: SpecialConditionData[] = [];

const ContractComplianceSpecialConditions = ({
  campaign,
  addButtonVariant,
  onSpecialConditionsUpdated,
  registerBeforeTaskModalSave,
  specialConditions = EMPTY_SPECIAL_CONDITIONS,
  task,
}: ContractComplianceSpecialConditionsProps) => {
  const [renderedSpecialConditions, setRenderedSpecialConditions] = useState<
    RenderedSpecialCondition[] | undefined
  >(undefined);

  const contractId = task.contractId ?? '';
  const campaignId = campaign.campaignId ?? '';

  const { constructConditionClauseText } = useConstructConditionClauseText({
    onSuccess: (conditionTextBlocks) => {
      setRenderedSpecialConditions(conditionTextBlocks);
    },
    onError: () => {
      setRenderedSpecialConditions(undefined);
    },
  });

  useEffect(() => {
    if (!campaignId || !contractId) return;

    if (specialConditions.length === 0) {
      setRenderedSpecialConditions((prevRendered) =>
        prevRendered?.length === 0 ? prevRendered : []
      );
      return;
    }

    constructConditionClauseText({
      campaignId,
      contractId,
      model: specialConditions,
    });
  }, [campaignId, constructConditionClauseText, contractId, specialConditions]);

  const onAddCustomCondition = useCallback(
    (customSpecialCondition: SpecialConditionData) => {
      onSpecialConditionsUpdated?.([...specialConditions, customSpecialCondition]);
    },
    [onSpecialConditionsUpdated, specialConditions]
  );

  const onDueDateUpdated = useCallback(
    async (
      conditionId: string,
      clauseId: string,
      newDueDateCondition: FormCondition | undefined,
      newDueDate: string | undefined
    ) => {
      onSpecialConditionsUpdated?.(
        specialConditions.map((condition) => {
          if (condition.id !== conditionId) return condition;

          return withUpdatedSpecialConditionDueDate(condition, clauseId, newDueDateCondition);
        })
      );

      setRenderedSpecialConditions((prevRendered) =>
        prevRendered?.map((renderedCondition) => {
          if (renderedCondition.id !== conditionId) return renderedCondition;

          return {
            ...renderedCondition,
            clauses: renderedCondition.clauses?.map((clause) => {
              if (clause.clauseId !== clauseId) return clause;

              return {
                ...clause,
                dueDate: newDueDate,
              };
            }),
          };
        })
      );
    },
    [onSpecialConditionsUpdated, specialConditions]
  );

  const onConditionDeleted = useCallback(
    (conditionId: string) => {
      onSpecialConditionsUpdated?.(
        specialConditions.filter((condition) => condition.id !== conditionId)
      );
    },
    [onSpecialConditionsUpdated, specialConditions]
  );

  const canShowEmptyState = useMemo(
    () => specialConditions.length === 0 && renderedSpecialConditions?.length === 0,
    [renderedSpecialConditions?.length, specialConditions.length]
  );

  const specialConditionIdToClauseNumberMap = useMemo(
    () =>
      renderedSpecialConditions?.reduce<{ [key: string]: string }>((lookup, renderedCondition) => {
        if (renderedCondition.id && renderedCondition.position) {
          lookup[renderedCondition.id] = renderedCondition.position;
        }

        for (const clause of renderedCondition.clauses ?? []) {
          if (clause.clauseId && clause.clausePosition) {
            lookup[clause.clauseId] = clause.clausePosition;
          }
        }

        return lookup;
      }, {}) ?? {},
    [renderedSpecialConditions]
  );

  return (
    <AgxSpacer direction="vertical" fill gap="spacing-xl">
      {canShowEmptyState && (
        <AgxEmptyState variant="minimal" label="No special conditions added." />
      )}
      <AgxSpacer direction="vertical" fill gap="spacing-xl" padding={{ bottom: 'md' }}>
        {renderedSpecialConditions?.map((renderedCondition) => (
          <SpecialConditionCollapsiblePanel
            key={renderedCondition.id ?? renderedCondition.conditionType}
            renderedSpecialCondition={renderedCondition}
            specialCondition={specialConditions.find(
              (condition) => condition.id === renderedCondition.id
            )}
            specialConditions={specialConditions}
            specialConditionIdToClauseNumberMap={specialConditionIdToClauseNumberMap}
            onDeleteCondition={onConditionDeleted}
            skipDeleteConfirmation
            deferDueDateSave
            campaignId={campaignId}
            contractId={contractId}
            onDueDateUpdated={onDueDateUpdated}
          />
        ))}

        <AddSpecialConditionButton
          id="contract-compliance-add-special-condition-button"
          label="Add Special Conditions"
          existingConditions={specialConditions}
          onAddSpecialCondition={onAddCustomCondition}
          campaignId={campaignId}
          buttonVariant={addButtonVariant}
          mode="inline"
          registerBeforeSave={registerBeforeTaskModalSave}
        />
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default ContractComplianceSpecialConditions;
